import React, {useMemo, useRef, useState} from 'react';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Chart,
    TooltipModel,
} from 'chart.js';
import {Line} from 'react-chartjs-2';
import PaymentRequestDynamicDiscountChartTooltip from 'components/pages/PaymentRequestPage/components/PaymentRequestDynamicDiscountChart/components/PaymentRequestDynamicDiscountChartTooltip';
import styles from './PaymentRequestDynamicDiscountChart.module.scss';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

interface IProps {
    discountDays: number;
    discountPercent: number;
    amount: number;
    invoiceDate?: Date;
}

const PaymentRequestDynamicDiscountChart = ({
    amount,
    discountDays,
    discountPercent,
    invoiceDate,
}: IProps) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipData, setTooltipData] = useState<TooltipModel<'line'> | null>(
        null,
    );
    const [tooltipPos, setTooltipPos] = useState<{
        top: number;
        left: number;
    } | null>(null);

    const chartRef = useRef<ChartJS<'line', any[], any>>(null);

    const {labels, values} = useMemo(() => {
        const singleDayDiscount = discountPercent / discountDays;
        const lbl = [];
        const val = [];

        for (let i = 0; i <= discountDays; i++) {
            lbl.push(i);
            val.push(singleDayDiscount * (discountDays - i));
        }

        return {labels: lbl, values: val};
    }, [discountDays, discountPercent]);

    const customTooltip = (context: {
        chart: Chart;
        tooltip: TooltipModel<'line'>;
    }) => {
        if (context.tooltip.opacity === 0) {
            // hide tooltip visibilty
            setTooltipVisible(false);
            return;
        }

        const chart = chartRef.current;
        if (!chart) return;

        const {canvas} = chart;
        if (canvas) {
            // enable tooltip visibilty
            setTooltipVisible(true);

            // set position of tooltip
            const left = Math.min(
                context.tooltip.x,
                (chartRef.current?.width || 0) - 120,
            );
            const top = context.tooltip.y;

            // handle tooltip multiple rerender
            if (tooltipPos?.top !== top || tooltipPos?.left !== left) {
                setTooltipPos({
                    top,
                    left,
                });
                setTooltipData(context.tooltip);
            }
        }
    };
    return (
        <div className={styles.container}>
            <h3>Your discount depending on the date of payment</h3>
            <p>
                Place your mouse pointer on the line to see the dynamic
                discounts.
            </p>
            <div className={styles.chartWrapper}>
                <Line
                    options={{
                        responsive: true,
                        elements: {
                            point: {
                                radius: 3,
                            },
                            line: {
                                borderWidth: 4,
                            },
                        },
                        plugins: {
                            legend: {
                                display: false,
                            },
                            title: {
                                display: false,
                            },
                            tooltip: {
                                enabled: false,
                                position: 'nearest',
                                external: customTooltip,
                            },
                        },
                        scales: {
                            y: {
                                ticks: {
                                    autoSkip: true,
                                    maxTicksLimit: 3,
                                    stepSize: discountPercent / 2,
                                },
                                max: discountPercent,
                            },
                            x: {
                                grid: {
                                    display: false,
                                },
                                ticks: {
                                    autoSkip: true,
                                },
                            },
                        },
                    }}
                    data={{
                        labels,
                        datasets: [
                            {
                                data: values,
                                borderColor: 'rgb(51, 51, 255)',
                                backgroundColor: 'rgba(51, 51, 255, 0.3)',
                                fill: true,
                            },
                        ],
                    }}
                    ref={chartRef}
                />

                <PaymentRequestDynamicDiscountChartTooltip
                    data={tooltipData}
                    position={tooltipPos}
                    visibility={tooltipVisible}
                    amount={amount}
                    invoiceDate={invoiceDate || new Date()}
                />
            </div>
        </div>
    );
};

export default PaymentRequestDynamicDiscountChart;
